import type { RouteObject } from 'react-router-dom';
import { Navigate, useRoutes } from 'react-router-dom';

import { AuditPage } from '@/app/pages/AuditPage';
import { BillingPage } from '@/app/pages/BillingPage';
import { LandingPage } from '@/app/pages/LandingPage';
import { MediaPage } from '@/app/pages/MediaPage';
import { OrdersPage } from '@/app/pages/OrdersPage';
import { ProductsPage } from '@/app/pages/ProductsPage';
import { StorePage } from '@/app/pages/StorePage';
import { SignUpPage } from '@/app/pages/SignUpPage';
import { accountRoutes } from '@/features/account-security';
import { contentRoutes } from '@/features/content';
import { catalogRoutes } from '@/features/catalog';
import { usersRoutes } from '@/features/access';
import { conversationsRoutes } from '@/features/conversations';
import { customersRoutes } from '@/features/customers';
import { channelStockRoutes } from '@/features/channel-stock';
import { inventoryRoutes } from '@/features/inventory';
import { loyaltyRoutes } from '@/features/loyalty';
import { promotionsRoutes } from '@/features/promotions';
import { settingsRoutes } from '@/features/store-settings';
import { AppLayout } from '@/shared/ui/AppLayout';
import { NotFoundPage } from '@/shared/ui/NotFoundPage';

import { HomeRoute } from './HomeRoute';
import { RequireAuth } from './RequireAuth';
import { RequireSection } from '@/shared/routing';

/**
 * Application route table.
 *
 * <p>Public routes (`/welcome`, `/sign-up`) render standalone. Everything else lives behind
 * {@link RequireAuth} — which gates on the session and renders the {@link AppLayout} shell.
 *
 * <p><b>Every business area is gated at the route</b>, not only in the sidebar. Filtering the menu
 * alone hides a section from the people who would click it and leaves it wide open to anyone who
 * types the URL — and staff do type URLs, from bookmarks and from links pasted by colleagues with
 * wider rights. Each guard names a key in `APP_SECTIONS`, the same registry the sidebar renders from,
 * so a hidden section is an unreachable one by construction.
 *
 * <p>The guards are UX, not security: the BFF enforces the same permission on every call it serves.
 * What they prevent is a portal that advertises doors it will not open, or half-renders a workspace
 * whose every request then fails.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const appRoutes: RouteObject[] = [
  // ── Public (unauthenticated) ────────────────────────────────────────────────
  { path: '/welcome', element: <LandingPage /> },
  { path: '/sign-up', element: <SignUpPage /> },

  // ── Authenticated application ─────────────────────────────────────────────────
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppLayout />,
        children: [
          // Resolves to the dashboard, the user's first available section, or the no-access
          // explanation — see HomeRoute.
          { index: true, element: <HomeRoute /> },
          catalogRoutes,
          promotionsRoutes,
          channelStockRoutes,
          inventoryRoutes,
          {
            path: 'products',
            element: (
              <RequireSection section="legacyProducts">
                <ProductsPage />
              </RequireSection>
            ),
          },
          {
            path: 'orders',
            element: (
              <RequireSection section="orders">
                <OrdersPage />
              </RequireSection>
            ),
          },
          customersRoutes,
          conversationsRoutes,
          {
            path: 'media',
            element: (
              <RequireSection section="media">
                <MediaPage />
              </RequireSection>
            ),
          },
          {
            // The store/locations workspace the Logistics sidebar entry points at (`/settings/store`
            // redirects here), so it answers to the same section key.
            path: 'store',
            element: (
              <RequireSection section="storeLocations">
                <StorePage />
              </RequireSection>
            ),
          },
          contentRoutes,
          loyaltyRoutes,
          usersRoutes,
          accountRoutes,
          {
            path: 'billing',
            element: (
              <RequireSection section="billing">
                <BillingPage />
              </RequireSection>
            ),
          },
          {
            path: 'audit',
            element: (
              <RequireSection section="audit">
                <AuditPage />
              </RequireSection>
            ),
          },
          settingsRoutes,
          { path: 'not-found', element: <NotFoundPage /> },
          // Unknown URLs fall back to the not-found page.
          { path: '*', element: <Navigate to="/not-found" replace /> },
        ],
      },
    ],
  },
];

/** Renders the active route for the current location. */
export function AppRoutes() {
  return useRoutes(appRoutes);
}
